import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { AlertSeverityEnum, CENTROCOSTO, ICentroCostoPagingRes, UseMutationsParams } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { getUrlParamsFetch, handleAxiosError  } from '@/shared';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchCentrosCostos = (
  params?: GetCentrosCostosParams,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ['centros-costos', ...Object.values(params || {})],
    queryFn: async () => {
      return getCentrosCostos(params);
    },
    enabled,
  });
};

export const useGetCentroCosto = (id: string) => {
  return useQuery({
    queryKey: ['centros-costos', id],
    queryFn: async () => {
      return getCentroCosto(id);
    },
  });
};

export const useCreateCentrosCostos = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createCentroCostos,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['centro-costos'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Centro costo creado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};


export const useUpdateCentrosCostos = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateCentrosCostos,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['centro-costos'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Centro costo actualizado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetCentrosCostosParams = {
  page?: number;
  page_size?: number;
  nombre?: string;
};

export type CreateCentrosCostosParams = Omit<CENTROCOSTO, 'id_centro_costo'>;
export type UpdateCentrosCostosParams = {
  id: string;
  data: CreateCentrosCostosParams;
};

export const getCentrosCostos = async (params?: GetCentrosCostosParams) => {
  const queryParams = getUrlParamsFetch({ ...params });
  return get<ICentroCostoPagingRes>(
    `${urlBackend}api/v1/centro-costo/?${queryParams}`,
    true
  );
};

export const getCentroCosto = async (id: string) => {
  try{
    return await get<CENTROCOSTO>(`${urlBackend}api/v1/centro-costo/${id}/`, true);
  }
  catch(error){
    return handleAxiosError(error);
  }
};

export const createCentroCostos = async (data: CreateCentrosCostosParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<CENTROCOSTO>(`${urlBackend}api/v1/centro-costo/`, data, true);
};

export const updateCentrosCostos = async ({
  id,
  data,
}: UpdateCentrosCostosParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<CENTROCOSTO>(`${urlBackend}api/v1/centro-costo/${id}/`, data, true);
};
